import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SideDetailViewService } from '@y42/core/container-with-side-detail-view';
import { IListViewComponent } from '@y42/core/list-view';
import { DateTimeService } from '@y42/date-time';
import { RowClickedEvent } from 'ag-grid-community';
import { distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';
import { AbstractJobService } from '../../../api/job.service';
import { JobListViewData, JobQuery, JobUIModel } from '../../../state';
import { JOB_PARENT_TYPE } from '../../services/job-parent-type.injection-token';
import { JobListViewContentAgGridConfigService } from './job-list-view-content-ag-grid-config.service';

@UntilDestroy()
@Component({
  selector: 'y42-job-list-view-content',
  templateUrl: './job-list-view-content.component.html',
  styleUrls: ['./job-list-view-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    JobListViewContentAgGridConfigService,
    {
      provide: JOB_PARENT_TYPE,
      useFactory: (jobService: AbstractJobService) => {
        return jobService.parentType;
      },
      deps: [AbstractJobService],
    },
  ],
})
export class JobListViewContentComponent implements IListViewComponent<JobListViewData> {
  constructor(
    private readonly jobService: AbstractJobService,
    private readonly jobQuery: JobQuery,
    private readonly dateTimeService: DateTimeService,
    private readonly sideDetailViewService: SideDetailViewService<void, JobUIModel['request_id']>,
    private readonly jobListViewContentAgGridConfigService: JobListViewContentAgGridConfigService,
  ) {
    this.sideDetailViewService.sidenavOpened$
      .pipe(
        distinctUntilChanged(),
        filter((isOpen) => !isOpen),
        tap(() => {
          const hasActive = !!this.jobQuery.getActive();
          if (hasActive) {
            this.jobService.setActive(null);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.jobQuery
      .selectActiveId()
      .pipe(
        switchMap((jobId) => {
          if (!jobId) {
            return of();
          }

          return this.sideDetailViewService.open(jobId);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  @Input() id = '';

  readonly max = this.dateTimeService.endOfDay(new Date());
  readonly selectedDateRangeForm = this.jobService.selectedDateRangeForm;
  readonly frameworkComponents = this.jobListViewContentAgGridConfigService.frameworkComponents;
  readonly columnDefs = this.jobListViewContentAgGridConfigService.columnDefs;
  readonly gridOptions = this.jobListViewContentAgGridConfigService.gridOptions;
  readonly defaultQuickFilters = this.jobListViewContentAgGridConfigService.defaultQuickFilters;
  readonly messages = this.jobListViewContentAgGridConfigService.messages;
  readonly quickFilterKey = this.jobListViewContentAgGridConfigService.quickFilterKey;

  gridRowClicked(event: RowClickedEvent<JobListViewData>) {
    if (!event.data) {
      return;
    }
    const id = event.data.request_id;
    this.jobService.setActive(id);
  }
}
